/**
 * Due Diligence Cache Service
 *
 * Centralized caching for building/unit due diligence dot values
 * Implements stale-while-revalidate pattern for optimal performance
 */

export interface DotValue {
  count: number;
  labels: string[];
  sources: any[];
  tooltipContent?: any[];
}

export interface DotValues {
  red: DotValue;
  orange: DotValue;
  green: DotValue;
  transparent: DotValue;
}

interface CacheEntry {
  data: DotValues;
  timestamp: number;
  version: number;
}

// Cache TTL: 15 minutes (consider data "fresh")
const CACHE_TTL = 15 * 60 * 1000;

// Stale TTL: 1 hour (still usable but should revalidate)
const STALE_TTL = 60 * 60 * 1000;

// Current cache version (increment when data structure changes)
const CACHE_VERSION = 1;

class DueDiligenceCacheService {
  private storage: Storage;
  private keyPrefix = 'dd_cache_';

  constructor() {
    // Use sessionStorage for per-session caching
    this.storage = sessionStorage;
  }

  /**
   * Get cache key for a building's dot values
   */
  private getBuildingKey(buildingId: string): string {
    return `${this.keyPrefix}building_${buildingId}`;
  }

  /**
   * Get cache key for a unit's dot values
   */
  private getUnitKey(unitId: string): string {
    return `${this.keyPrefix}unit_${unitId}`;
  }

  /**
   * Store dot values in cache
   */
  set(key: string, data: DotValues): void {
    try {
      const entry: CacheEntry = {
        data,
        timestamp: Date.now(),
        version: CACHE_VERSION,
      };
      this.storage.setItem(key, JSON.stringify(entry));
    } catch (e) {
      console.error('[DueDiligenceCache] Error saving to cache:', e);
      // If storage is full, clear old entries
      this.cleanup();
    }
  }

  /**
   * Get dot values from cache
   * Returns null if not found or expired
   */
  get(key: string, allowStale = false): DotValues | null {
    try {
      const item = this.storage.getItem(key);
      if (!item) return null;

      const entry: CacheEntry = JSON.parse(item);

      // Check version compatibility
      if (entry.version !== CACHE_VERSION) {
        this.storage.removeItem(key);
        return null;
      }

      const age = Date.now() - entry.timestamp;

      // Check if expired
      if (age > STALE_TTL) {
        this.storage.removeItem(key);
        return null;
      }

      // If stale but within stale window
      if (age > CACHE_TTL && !allowStale) {
        return null;
      }

      return entry.data;
    } catch (e) {
      console.error('[DueDiligenceCache] Error reading from cache:', e);
      return null;
    }
  }

  /**
   * Check if cache entry is stale (but still valid)
   */
  isStale(key: string): boolean {
    try {
      const item = this.storage.getItem(key);
      if (!item) return false;

      const entry: CacheEntry = JSON.parse(item);
      const age = Date.now() - entry.timestamp;

      return age > CACHE_TTL && age <= STALE_TTL;
    } catch (e) {
      return false;
    }
  }

  /**
   * Store building dot values
   */
  setBuildingDotValues(buildingId: string, data: DotValues): void {
    this.set(this.getBuildingKey(buildingId), data);
  }

  /**
   * Get building dot values
   */
  getBuildingDotValues(buildingId: string, allowStale = false): DotValues | null {
    return this.get(this.getBuildingKey(buildingId), allowStale);
  }

  /**
   * Check if building cache is stale
   */
  isBuildingStale(buildingId: string): boolean {
    return this.isStale(this.getBuildingKey(buildingId));
  }

  /**
   * Store unit dot values
   */
  setUnitDotValues(unitId: string, data: DotValues): void {
    this.set(this.getUnitKey(unitId), data);
  }

  /**
   * Get unit dot values
   */
  getUnitDotValues(unitId: string, allowStale = false): DotValues | null {
    return this.get(this.getUnitKey(unitId), allowStale);
  }

  /**
   * Invalidate cache for a specific building (and its units)
   */
  invalidateBuilding(buildingId: string, unitIds?: string[]): void {
    this.storage.removeItem(this.getBuildingKey(buildingId));

    if (unitIds) {
      unitIds.forEach(unitId => {
        this.storage.removeItem(this.getUnitKey(unitId));
      });
    }
  }

  /**
   * Clear all due diligence cache
   */
  clearAll(): void {
    const keysToRemove: string[] = [];

    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key?.startsWith(this.keyPrefix)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => this.storage.removeItem(key));
    console.log(`[DueDiligenceCache] Cleared ${keysToRemove.length} cache entries`);
  }

  /**
   * Clean up expired entries
   */
  cleanup(): void {
    const keysToRemove: string[] = [];
    const now = Date.now();

    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (!key?.startsWith(this.keyPrefix)) continue;

      try {
        const item = this.storage.getItem(key);
        if (!item) continue;

        const entry: CacheEntry = JSON.parse(item);
        const age = now - entry.timestamp;

        // Remove if expired or wrong version
        if (age > STALE_TTL || entry.version !== CACHE_VERSION) {
          keysToRemove.push(key);
        }
      } catch (e) {
        // Remove corrupted entries
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => this.storage.removeItem(key));

    if (keysToRemove.length > 0) {
      console.log(`[DueDiligenceCache] Cleaned up ${keysToRemove.length} expired entries`);
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): {
    total: number;
    fresh: number;
    stale: number;
    buildings: number;
    units: number;
  } {
    const stats = {
      total: 0,
      fresh: 0,
      stale: 0,
      buildings: 0,
      units: 0,
    };

    const now = Date.now();

    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (!key?.startsWith(this.keyPrefix)) continue;

      stats.total++;

      if (key.includes('_building_')) stats.buildings++;
      if (key.includes('_unit_')) stats.units++;

      try {
        const item = this.storage.getItem(key);
        if (!item) continue;

        const entry: CacheEntry = JSON.parse(item);
        const age = now - entry.timestamp;

        if (age <= CACHE_TTL) {
          stats.fresh++;
        } else if (age <= STALE_TTL) {
          stats.stale++;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    return stats;
  }
}

// Export singleton instance
export const dueDiligenceCache = new DueDiligenceCacheService();

// Export helper to create initial dot values
export const createEmptyDotValues = (): DotValues => ({
  red: { count: 0, labels: [], sources: [] },
  orange: { count: 0, labels: [], sources: [] },
  green: { count: 0, labels: [], sources: [] },
  transparent: { count: 0, labels: [], sources: [] },
});
